import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowExpandIcon,
  Bathtub01Icon,
  BedIcon,
  Time04Icon,
  UserCircleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { Badge, BadgeSize, BadgeVariant, Show } from "shared/components";
import { TruncateWithTooltip } from "shared/components/TruncateWithTooltip";
import { UnitCardToggles } from "./UnitCardToggles";
import { Unit } from "types/landlord/address";
import { UnitCardField } from "../../visibilityConstants";

const addressLine = (unit: Unit) => {
  const address = unit.address ?? unit.property?.address;
  if (!address) return null;

  return [
    address.address1,
    address.city,
    [address.state_or_province, address.postal_code].filter(Boolean).join(" ")
  ]
    .filter(Boolean)
    .join(", ");
};

export const UnitCardBody = ({
  unit,
  shows
}: {
  unit: Unit;
  shows: (field: string) => boolean;
}) => {
  const address = addressLine(unit);
  const leadsLast7Days = unit.leads_last_7_days ?? 0;
  const waitlistLeads = unit.waitlist_leads ?? 0;
  const showsMetadata =
    shows(UnitCardField.Beds) ||
    shows(UnitCardField.Baths) ||
    shows(UnitCardField.SquareFootage);
  const showsLeadsRow =
    shows(UnitCardField.Leads) || shows(UnitCardField.Waitlist);

  return (
    <div className="space-y-3 border-t border-alpha-100 px-5 py-4">
      <div className="space-y-1">
        <Show if={shows(UnitCardField.UnitName)}>
          <TruncateWithTooltip
            content={unit.title}
            className="text-xl font-bold text-content-1000"
          />
        </Show>
        <Show if={shows(UnitCardField.PropertyName)}>
          {unit.property?.name ? (
            <TruncateWithTooltip
              content={unit.property.name}
              className="text-sm font-medium text-content-1000"
            />
          ) : (
            <div className="h-5" />
          )}
        </Show>
        <Show if={shows(UnitCardField.PropertyAddress)}>
          {address ? (
            <TruncateWithTooltip
              content={address}
              className="text-sm text-content-500"
            />
          ) : (
            <div className="h-5" />
          )}
        </Show>
      </div>

      <Show if={showsMetadata}>
        <hr className="border-alpha-40" />
        <div className="flex h-6 items-center gap-2 py-0.5">
          <Show if={shows(UnitCardField.Beds)}>
            <span className="flex items-center gap-2 text-sm font-medium text-content-1000">
              <HugeiconsIcon
                size={16}
                icon={BedIcon}
                className="text-content-500"
              />
              {unit.plan?.beds ?? 0} bed
            </span>
          </Show>
          <Show if={shows(UnitCardField.Beds) && shows(UnitCardField.Baths)}>
            <span className="h-5 w-px bg-alpha-40" />
          </Show>
          <Show if={shows(UnitCardField.Baths)}>
            <span className="flex items-center gap-2 text-sm font-medium text-content-1000">
              <HugeiconsIcon
                size={16}
                icon={Bathtub01Icon}
                className="text-content-500"
              />
              {unit.plan?.baths ?? 0} bath
            </span>
          </Show>
          <Show
            if={
              shows(UnitCardField.SquareFootage) &&
              (shows(UnitCardField.Beds) || shows(UnitCardField.Baths))
            }
          >
            <span className="h-5 w-px bg-alpha-40" />
          </Show>
          <Show if={shows(UnitCardField.SquareFootage)}>
            <span className="flex items-center gap-2 text-sm font-medium text-content-1000">
              <HugeiconsIcon
                size={16}
                icon={ArrowExpandIcon}
                className="text-content-500"
              />
              {unit.plan?.sqft ?? 0} sqft
            </span>
          </Show>
        </div>
      </Show>
      <Show
        if={
          shows(UnitCardField.AllowApplications) ||
          shows(UnitCardField.ListUnit)
        }
      >
        <hr className="border-alpha-40" />
        <UnitCardToggles unit={unit} shows={shows} />
      </Show>
      <Show if={showsLeadsRow}>
        <hr className="border-alpha-40" />
        <div className="flex items-center gap-2.5">
          <Show if={shows(UnitCardField.Leads)}>
            <span className="flex items-center gap-1 text-sm font-medium text-content-1000">
              <HugeiconsIcon
                size={16}
                icon={UserCircleIcon}
                className="text-content-400"
              />
              {leadsLast7Days} leads
            </span>
          </Show>
          <Show if={shows(UnitCardField.Leads) && leadsLast7Days > 0}>
            <Badge variant={BadgeVariant.PRIMARY} size={BadgeSize.SM}>
              <span className="font-bold">{leadsLast7Days}</span>
              <span className="font-medium"> last 7 days</span>
            </Badge>
          </Show>
          <Show if={shows(UnitCardField.Waitlist) && Boolean(unit.waitlist)}>
            <span className="border-l border-alpha-40 pl-2.5 flex items-center gap-2 text-sm font-medium text-content-1000">
              <HugeiconsIcon
                size={16}
                icon={Time04Icon}
                className="text-content-400"
              />
              {waitlistLeads} waitlist
            </span>
          </Show>
        </div>
      </Show>
    </div>
  );
};
